import type { SerializedXYWH } from '@blocksuite/global/gfx';
import type { BaseElementProps } from '@blocksuite/std/gfx';
import { field, GfxPrimitiveElementModel } from '@blocksuite/std/gfx';
import * as Y from 'yjs';

export type MermaidThemeMode = 'auto' | 'light' | 'dark';
export type MermaidFitMode = 'content' | 'width' | 'free';

export type MermaidElementProps = BaseElementProps & {
  source: Y.Text;
  schemaVersion?: number;
  themeMode?: MermaidThemeMode;
  fitMode?: MermaidFitMode;
};

/**
 * Persisted Mermaid diagram source and canvas geometry.
 *
 * Rendering and editor state deliberately live outside this model: SVG is
 * derived from source, while Diagram/Code/Split is transient UI state.
 */
export class MermaidElementModel extends GfxPrimitiveElementModel<MermaidElementProps> {
  get type() {
    return 'mermaid';
  }

  static propsToY(props: Record<string, unknown>) {
    if (typeof props.source === 'string') {
      props.source = new Y.Text(props.source);
    }

    return props as MermaidElementProps;
  }

  // Rotation is intentionally disabled for V1. Connector geometry and the
  // renderer therefore use the native rectangular primitive bounds.
  get rotate() {
    return 0;
  }

  set rotate(_: number) {}

  @field('content' as MermaidFitMode)
  accessor fitMode: MermaidFitMode = 'content';

  @field(1)
  accessor schemaVersion: number = 1;

  @field()
  accessor source: Y.Text = new Y.Text();

  @field('auto' as MermaidThemeMode)
  accessor themeMode: MermaidThemeMode = 'auto';

  @field()
  accessor xywh: SerializedXYWH = '[0,0,480,320]';
}
